import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { useTranslation } from "react-i18next";
import makeStyles from "@material-ui/core/styles/makeStyles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Box, TextField, Theme, Typography } from "@material-ui/core";
import { useAppDispatch } from "../app/appHooks";
import {
  recommendationMaxCharacters,
  ReportStatusTypes,
  ReportTypes,
} from "../../helpers/constants";
import {
  createUpdateReportRecommendation,
  generateReport,
} from "./reportsThunks";
import { getMonth } from "../../helpers/utils";
import { formatDateTime } from "../../helpers/datetime";
import { setReports } from "./reportsSlice";
import { useSelector } from "react-redux";
import { IAppState } from "../../helpers/store";
import { refreshReportList } from "./ReportItem";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonBar: {
      padding: `${theme.spacing(3)}px ${theme.spacing(6)}px`,
      display: "flex",
      justifyContent: "center",
      gap: theme.spacing(4),
    },
    dialogTitleBox: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      paddingLeft: "130px",
      paddingRight: "130px",
    },
    dialogTitle: {
      fontWeight: "bold",
      textAlign: "center",
    },
    dialogContentBox: {
      paddingTop: theme.spacing(2),
      marginLeft: "25px",
      marginRight: "25px",
    },
    descriptionText: {
      fontWeight: "bold",
      marginLeft: theme.spacing(6),
      fontSize: 14,
    },
    dialogButtons: {
      width: "120px",
      height: "35px",
    },
    dateText: {
      fontWeight: "bold",
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      color: theme.palette.grey[600],
    },
    recommendationField: {
      "& .MuiInputBase-root": {
        border: `1px solid`,
        padding: "14px 14px",
        fontFamily: "GothamRoundedBook, Helvetica, Arial, sans-serif",
        lineHeight: "0.95em",
        fontSize: 14,
        alignItems: "flex-start",
        display: "flex",
      },
      "& .MuiInputBase-inputMultiline": {
        boxSizing: "border-box",
        padding: 0,
        margin: 0,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
    timeUpdatedText: {
      marginRight: theme.spacing(6),
      color: theme.palette.text.secondary,
      fontSize: "12px",
      textAlign: "right",
    },
    charCounter: {
      marginLeft: theme.spacing(6),
      fontSize: "12px",
      textAlign: "right",
    },
    charCountOver: {
      color: theme.palette.error.main,
      fontWeight: "bold",
      transition: "color 0.2s",
    },
    charCountNormal: {
      color: theme.palette.text.secondary,
    },
  })
);

export const RecommendationDialog = (props: iProps) => {
  /* ---- Hooks ---- */
  const classes = useStyles();
  const { t } = useTranslation();
  const { toggle, onClose } = props;
  const dispatch = useAppDispatch();

  const reports = useSelector((state: IAppState) => state.reportsState.reports);

  const [recommendation, setRecommendation] = useState("");

  const reportDateString = `${props.reportDate.getFullYear()}-${(
    props.reportDate.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}-01`;

  const formattedReportDate =
    props.reportDate.getFullYear() +
    " " +
    getMonth(props.reportDate.getMonth(), props.userLanguage);

  useEffect(() => {
    setRecommendation(props.reportRecommendation);
  }, [props.reportRecommendation]);

  const handleSubmit = () => {
    if (props.unit_id && props.community_id) {
      dispatch(
        createUpdateReportRecommendation({
          unit_id: props.unit_id,
          community_id: props.community_id,
          report_type: ReportTypes.ACTIVITY,
          report_date: reportDateString,
          report_recommendation: recommendation,
        })
      );
    }
    if (
      props.reportStatus === ReportStatusTypes.COMPLETED &&
      props.version === "" &&
      props.community_id
    ) {
      const messageData = {
        report_type: ReportTypes.ACTIVITY,
        report_date: reportDateString,
        community_id: props.community_id,
        unit_id: props.unit_id,
      };
      if (reports) {
        const filtered = refreshReportList(reports, messageData);
        dispatch(setReports(filtered));
      }

      dispatch(generateReport(messageData));
    }
    onClose();
  };

  const closeDialog = () => {
    setRecommendation(props.reportRecommendation);
    onClose();
  };

  const handleRecommendationChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    let { value } = e.target;
    setRecommendation(value);
  };

  return (
    <div>
      <Dialog
        open={toggle}
        onClose={closeDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            width: 800,
            minWidth: 800,
            maxWidth: 800,
            height: 640,
            minHeight: 640,
            maxHeight: 640,
          },
        }}
      >
        <DialogTitle className={classes.dialogTitleBox} id="alert-dialog-title">
          <Typography className={classes.dialogTitle}>
            {t("recommendation_title")}
          </Typography>
        </DialogTitle>
        <Typography id="alert-dialog-date" className={classes.dateText}>
          {formattedReportDate}
        </Typography>
        <Typography
          id="alert-dialog-description"
          className={classes.descriptionText}
        >
          {t("recommendation_description")}
        </Typography>
        <DialogContent className={classes.dialogContentBox}>
          <TextField
            fullWidth
            multiline
            variant="outlined"
            onChange={handleRecommendationChange}
            value={recommendation}
            className={classes.recommendationField}
            placeholder={t("enter_recommendation")}
            minRows={25}
            maxRows={25}
          />
        </DialogContent>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="caption" className={classes.charCounter}>
            <span className={classes.charCountNormal}>Characters: </span>
            <span
              className={
                recommendation.length > recommendationMaxCharacters
                  ? classes.charCountOver
                  : classes.charCountNormal
              }
            >
              {recommendation.length}
            </span>
            <span className={classes.charCountNormal}>
              / {recommendationMaxCharacters}
            </span>
          </Typography>

          {props.timeUpdated && (
            <Typography variant="caption" className={classes.timeUpdatedText}>
              {t("last_updated")}: {formatDateTime(props.timeUpdated)}
            </Typography>
          )}
        </Box>
        <DialogActions className={classes.buttonBar}>
          <Button
            onClick={() => {
              closeDialog();
            }}
            color="default"
            variant="contained"
            className={classes.dialogButtons}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            className={classes.dialogButtons}
            disabled={
              recommendation === props.reportRecommendation ||
              recommendation.length > recommendationMaxCharacters
            }
          >
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
export default RecommendationDialog;

export interface iProps {
  toggle: boolean;
  unit_id: string;
  community_id: string | null;
  reportDate: Date;
  userLanguage: string | null | undefined;
  reportRecommendation: string;
  timeUpdated: string;
  onClose: () => void;
  reportStatus: string | null;
  version: string | null;
  setButtonText: Dispatch<SetStateAction<string>>;
}
